/**
 * 표 머리글 정렬 — `DataTable` 의 비교 규칙 한 곳(eric 2026-09-30 「게시판 테이블의 컬럼 헤더 클릭하면..
 * 내림 차순 정렬.. 한번 더 클릭하면 오름 차순 정렬」).
 *
 * 🔴 **두 상태 토글이다.** 새 열을 누르면 내림차순, 같은 열을 다시 누르면 오름차순, 또 누르면 내림차순.
 *    「정렬 없음」으로 돌아가는 세 번째 상태는 없다 — eric 은 둘만 말했다.
 * 🔴 **값이 없으면(`null` · `undefined` · 빈 글자 · `—` · 못 읽은 시각/수) 방향과 무관하게 맨 뒤다.**
 *    오름차순에서 앞으로 오면 「모른다」가 「가장 작다/오래됐다」인 척 맨 위에 선다.
 * 🔴 **안정 정렬** — 같은 값은 받은 차례 그대로다(방향을 바꿔도 뒤집지 않는다). `Array.prototype.sort` 는
 *    ES2019 부터 안정이다.
 * ⚠ 시각은 **글자가 아니라 시각으로** 비교한다 — 오프셋 표기(`Z` · `+00:00` · `+09:00`)가 섞이면 글자 순이
 *   시각 순이 아니다.
 */

export type SortDirection = 'desc' | 'asc'

/** 표 하나의 정렬 상태 — 열 키 하나 + 방향 */
export interface TableSort {
  key: string
  direction: SortDirection
}

/** 정렬 값 종류. `time` = ISO 문자열 또는 epoch ms · `number` = 수 · `text` = 글자 */
export type SortKind = 'text' | 'number' | 'time'

/** `sortValue` 가 돌려줄 수 있는 것. 값 없음은 `null`/`undefined` */
export type SortValue = string | number | null | undefined

/** 열에서 정렬에 쓰는 부분만 */
export interface SortableColumn<Row> {
  key: string
  sort?: SortKind | ((a: Row, b: Row) => number) | undefined
  sortValue?: ((row: Row) => SortValue) | undefined
}

/*
 * 글자 비교 — 한국어 콜레이터. `numeric` 을 켰다: `B-2` 가 `B-10` 보다 앞에 서게(글자 순이면 거꾸로다).
 * 모듈 한 벌만 짓는다(매 비교마다 `localeCompare` 가 콜레이터를 새로 지으면 느리다).
 */
const COLLATOR = new Intl.Collator('ko', { numeric: true })

/** 비교할 수 있는 값으로 — 못 읽으면 `null`(= 맨 뒤) */
function normalize(kind: SortKind, value: SortValue): string | number | null {
  if (value === null || value === undefined) return null
  if (kind === 'text') {
    const text = String(value).trim()
    return text === '' || text === '—' ? null : text
  }
  if (kind === 'number') {
    const n = typeof value === 'number' ? value : Number(value)
    return typeof value === 'string' && value.trim() === '' ? null : Number.isFinite(n) ? n : null
  }
  const ms = typeof value === 'number' ? value : Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

/** 이 열을 머리글로 정렬할 수 있나 — 종류를 줬으면 값 읽는 법(`sortValue`)도 있어야 한다 */
export function isSortable<Row>(column: SortableColumn<Row>): boolean {
  if (typeof column.sort === 'function') return true
  return column.sort !== undefined && column.sortValue !== undefined
}

/** 머리글을 눌렀을 때 다음 상태 — 새 열은 내림차순, 같은 열은 방향만 뒤집는다 */
export function nextSort(current: TableSort | null, key: string): TableSort {
  if (current?.key !== key) return { key, direction: 'desc' }
  return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' }
}

/**
 * 줄을 정렬한 **새 배열**을 돌려준다(받은 배열은 건드리지 않는다).
 * 정렬 상태가 없거나 그 열이 정렬 불가면 받은 차례 그대로(새 배열 아님 — 받은 것 그대로)다.
 *
 * ⚠ 비교 함수(`sort: (a, b) => …`)를 준 열은 **오름차순 기준 비교**를 준 것으로 본다 — 내림차순이면 부호만
 *   뒤집는다. 그 경우 값 없음의 자리는 그 함수가 정한다(이 모듈의 「맨 뒤」 규칙이 닿지 않는다).
 */
export function sortRows<Row>(
  rows: readonly Row[],
  columns: readonly SortableColumn<Row>[],
  sort: TableSort | null,
): readonly Row[] {
  if (sort === null) return rows
  const column = columns.find((c) => c.key === sort.key)
  if (column === undefined || !isSortable(column)) return rows
  const sign = sort.direction === 'desc' ? -1 : 1
  const spec = column.sort

  if (typeof spec === 'function') {
    return [...rows].sort((a, b) => sign * spec(a, b))
  }
  const kind = spec ?? 'text'
  const read = column.sortValue ?? (() => null)
  // 값을 한 번씩만 읽는다 — 비교마다 `Date.parse` 를 다시 돌리지 않게
  const keyed = rows.map((row, index) => ({ row, index, value: normalize(kind, read(row)) }))
  keyed.sort((a, b) => {
    if (a.value === null || b.value === null) {
      if (a.value === b.value) return a.index - b.index
      return a.value === null ? 1 : -1 // 방향 무관 — 값 없음은 뒤
    }
    const base =
      typeof a.value === 'number' && typeof b.value === 'number'
        ? a.value - b.value
        : COLLATOR.compare(String(a.value), String(b.value))
    return base === 0 ? a.index - b.index : sign * base
  })
  return keyed.map((k) => k.row)
}
